import { FunctionComponent, lazy, Suspense, useEffect, useState } from 'react';
import Form from 'react-bootstrap/Form';
import config from '@config';

// Lazy import the TinyMceEditor component
// @ts-ignore
const TinyMceEditor = lazy(() => import('@tinymce/tinymce-react').then(module => ({ default: module.Editor })));

// Lazy import CodeMirror editor component
// @ts-ignore
const CodeMirrorEditor: any = lazy(() => import('@uiw/react-codemirror').then(module => ({ default: module.default })));

export interface RichTextTextEditorProps
{
    /** The name of the field. */
    field: string;
    /** The label for the editor. */
    label?: string;
    /** Is this field required? */
    isRequired?: boolean;
    /** Is the editing enabled? */
    isEnabled?: boolean;
    /** The placeholder for the field value. */
    placeholder?: string;
    /** The number of rows in the editor. */
    rows?: number;
    /** The value of the field. */
    value?: string;
    /** Fired when the field value changes in the editor. */
    onValueChanged?: (field: string, value: string) => void;
    /** CSS class for the control. */
    className?: string;

    /** Enables the HTML toggle switch. */
    enableHtmlToggle?: boolean;
    /** Default editor mode. */
    defaultMode?: 'rich' | 'html';
    /** Notify external listeners when mode changes. */
    onModeChange?: (mode: 'rich' | 'html') => void;
}

/**
 * Component: a wrapper around the TinyMCE editor with an optional HTML mode (CodeMirror)
 */
const RichTextTextEditor: FunctionComponent<RichTextTextEditorProps> = (props) =>
{
    const rows = props.rows ?? 6;
    const isEnabled = props.isEnabled ?? true;
    const [mode, setMode] = useState<'rich' | 'html'>(props.defaultMode ?? 'rich');
    const [htmlExtensions, setHtmlExtensions] = useState<any[]>([]);

    useEffect(() =>
    {
        let mounted = true;
        (async () =>
        {
            const { html } = await import('@codemirror/lang-html');
            if (mounted) setHtmlExtensions([html()]);
        })();

        return () => { mounted = false; };
    }, []);

    const handleToggle = (checked: boolean) =>
    {
        const nextMode = checked ? 'html' : 'rich';
        setMode(nextMode);
        props.onModeChange?.(nextMode);
    };

    const imagesUploadUrl = `${config.apiUrl.replace(/graphql\/?$/, '')}uploads/richtext/`;

    /** Render */
    return (
        <div className={props.className ?? ''}>
            {(props.enableHtmlToggle ?? true) &&
                <div className='d-flex justify-content-end mb-2'>
                    <Form.Check
                        type='switch'
                        id={`${props.field}-html-toggle`}
                        label='HTML'
                        checked={mode === 'html'}
                        onChange={(e) => handleToggle(e.currentTarget.checked)}
                    />
                </div>
            }

            <Suspense fallback={<div>Loading Editor...</div>}>
                {mode === 'html' ? (
                    <CodeMirrorEditor
                        value={props.value ?? ''}
                        // @ts-ignore: editable is supported by @uiw/react-codemirror
                        editable={isEnabled}
                        height={`${rows * 60}px`}
                        placeholder={props.placeholder}
                        extensions={htmlExtensions}
                        onChange={(val: string) => { if (props.onValueChanged) props.onValueChanged(props.field, val); }}
                    />
                ) : (
                    <TinyMceEditor
                        tinymceScriptSrc='https://cdnjs.cloudflare.com/ajax/libs/tinymce/7.9.0/tinymce.min.js'
                        value={props.value ?? ''}
                        disabled={!isEnabled}
                        init={{
                            license_key: 'gpl',
                            height: rows * 60,
                            placeholder: props.placeholder,
                            menubar: true,
                            plugins: [
                                'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                                'anchor', 'searchreplace', 'visualblocks', 'fullscreen', 'paste',
                                'insertdatetime', 'media', 'table', 'help', 'wordcount'
                            ],
                            toolbar: 'undo redo | blocks | ' +
                                'bold italic forecolor | alignleft aligncenter ' +
                                'alignright alignjustify | bullist numlist outdent indent | ' +
                                'image | removeformat | help',
                            content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                            automatic_uploads: true,
                            images_upload_url: imagesUploadUrl,
                            images_upload_credentials: false,
                            paste_data_images: true,
                        }}
                        // @ts-ignore
                        onEditorChange={(content: string) => { if (props.onValueChanged) props.onValueChanged(props.field, content); }}
                    />
                )}
            </Suspense>
        </div>
    );
}

export default RichTextTextEditor;